import React, { useContext, useEffect, useState } from "react";
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ObjectIndex from "@components/objects/ObjectIndex";
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ObjectShow from "@components/objects/ObjectShow";
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import ObjectColumn from "@components/objects/ObjectColumn";
import { ObjectPrimaryInfo } from "@components/objects/ObjectPrimaryInfo";
import Avatar from "@mui/joy/Avatar";
import { ObjectContext } from "@providers/ObjectProvider";
import { ensureDateObject, formatAbsoluteDate, formatDate, isValidDate } from "@utils/dates";
import ObjectPreview from "@components/objects/ObjectPreview";
import { generateAddress } from "@utils/generateAddress";
import { CardOverflow, List, ListDivider, ListItem, ListItemContent, ListItemDecorator, Table } from "@mui/joy";
import AbstractObjectRelationship from "@components/objects/AbstractObjectRelationship";
import UserAccessCard from "@components/objects/UserAccessCard";
import { intervalToDuration } from "date-fns";
import ObjectEdit from "@components/objects/ObjectEdit";
import { ObjectPrimaryInfoEdit } from "@components/objects/ObjectPrimaryInfoEdit";
import { ObjectRelationshipEdit } from "@components/objects/ObjectRelationshipEdit";
import { countries } from "@utils/selectOptions";
import { ObjectRelationship } from "@components/objects/ObjectRelationship";
import SearchSelect from "@components/SearchSelect";
import { CheckPermission } from "@components/CheckPermission";
import { getBusinessLogo, useDeleteBusiness, useGetBusiness, useUpdateBusiness } from "@api/businessService";
import { ContextualHelp } from "@components/ContextualHelp";



// import { DynamicFieldData } from "./dynamic-control-types";

const fields /*: DynamicFieldData[]*/ = [
    {
        name: 'logo',
        type: 'file',
        label: 'Logo',
        scope: 'logo',
        url: (object) => getBusinessLogo(object)?.url,
    },

    {
        name: 'name',
        type: 'text',
        placeholder: 'e.g. Marsha\'s Truffles',
        showRequired: true,
        config: {
            required: true,
        }
    },
    {name: 'dba_name', label: 'DBA Name', type: 'text', placeholder: 'e.g. Confections by Marsha'},
    {
        name: 'business_type_id',
        type: 'relationship',
        endpoint: 'business-type',
        defaultProp: 'business_type',
        loadAll: true,
    },
    {
        name: 'primary_contact_id',
        type: 'relationship',
        endpoint: 'contacts',
        defaultProp: 'owner',
    },
    {
        name: 'do_not_contact',
        label: 'Communications',
        type: 'date_boolean',
        inputLabel: 'Do Not Contact',
    },
    {name: 'phone', type: 'text', placeholder: 'e.g. (000) 000-0000'},
    {name: 'fax', type: 'text', placeholder: 'e.g. (000) 000-0000'},
    {name: 'email', type: 'text', placeholder: 'e.g. marsha@tastytruffles.net'},

    {name: 'address_mail', label: 'Mailing Address', type: 'text', placeholder: 'e.g. 123 Main Street'},
    {name: 'address_2_mail', label: <>Mailing Address <br/>(cont.)</>, type: 'text', placeholder: 'e.g. Suite A'},
    {name: 'country_mail', label: 'Mailing Country', type: 'select', options: countries, defaultValue: ({tenant}) => tenant.country},
    {
        name: 'zip_postal_mail',
        label:<>Mailing Zip/<wbr/>Postal</>,
        type: 'text',
        placeholder: 'e.g. 61325',
        defaultValue: ({tenant}) => tenant.postal,
        /*
        onChange: (value, formState_wip) => {
            prefillCityProvince
        }
        */
    },
    {name: 'city_mail', label: 'Mailing City', type: 'text', placeholder: 'e.g. MainStreet', defaultValue: ({tenant}) => tenant.city,},
    {name: 'state_province_mail', label: 'Mailing State', type: 'text', placeholder: 'e.g. IL', defaultValue: ({tenant}) => tenant.state,},

    {
        name: 'membership_level_id',
        label: 'Membership',
        type: 'relationship',
        endpoint: 'membership-levels',
        defaultProp: 'membership_level',
        loadAll: true,
    },
    {name: 'tax_id', label: 'Tax ID', type: 'text', placeholder: 'e.g. 999999999'},
    // Tax Rate
    {name: 'url', label: 'Website', type: 'text', placeholder: 'e.g. https://tastytruffles.net'},
    {name: 'facebook_url', label: 'Facebook', type: 'text', placeholder: 'e.g. https://www.facebook.com/marshastastytruffles'},
    {name: 'instagram_url', label: 'Instagram', type: 'text', placeholder: 'e.g. https://www.instagram.com/marshastastytruffles'},
    {name: 'twitter_url', label: 'X (Twitter)', type: 'text', placeholder: 'e.g. https://twitter.com/marshastastytruffles'},
    {name: 'linkedin_url', label: 'LinkedIn', type: 'text', placeholder: 'e.g. https://www.linkedin.com/marshastastytruffles'},
    {name: 'youtube_url', label: 'YouTube', type: 'text', placeholder: 'e.g. https://www.youtube.com/@marshastastytruffles'},
    {name: 'yelp_url', label: 'Yelp', type: 'text', placeholder: 'e.g. https://www.yelp.com/biz/tastytruffles'},
    {name: 'description', label: 'Description', type: 'text', placeholder: 'Enter a description...'},
    {
        name: 'not_for_profit',
        label: 'Not for Profit',
        type: 'boolean',
        // inputLabel: '',
    },
    {
        name: 'not_in_district',
        // label: 'In District',
        label: <ContextualHelp slug="not_in_district">In District</ContextualHelp>,
        type: 'boolean',
        reverse: true,
        // inputLabel: '',
    },
    {name: 'open_date', label: 'Opened', type: 'date', placeholder: 'e.g. 05/23/2016'},
    {name: 'close_date', label: 'Closed', type: 'date', placeholder: 'e.g. 05/23/2016'},
    {
        name: 'moved_in_date',
        label: <ContextualHelp slug="moved_in_date">Relocated <br/>In Date</ContextualHelp>,
        // label: 'Relocated In',
        type: 'date',
        placeholder: 'e.g. 05/23/2016',
    },
    {
        name: 'moved_out_date',
        label: <ContextualHelp slug="moved_out_date">Relocated <br/>Out Date</ContextualHelp>,
        // label: 'Relocated Out',
        type: 'date',
        placeholder: 'e.g. 05/23/2016',
    },

    // notes
    {
        name: 'demographics',
        label: 'Demographics',
        type: 'demographics',
        suffix: '-owned',
    },

];

const CardHeader = () => {
    const data = useContext(ObjectContext);
    const avatarInitials = data.name ? data.name.split(' ').map(i => i.charAt(0)).join('').toUpperCase() : '';

    return (
    <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={2}
        p={1.5}
        pt={0}
    >
        <Avatar size="lg" sx={{'--Avatar-size': '5rem'}} src={data?.profile_image?.url}>{avatarInitials}</Avatar>
        <Box sx={{flex: 1}}>
            <Typography fontSize={'md'} fontWeight={'bold'}>{data.name}</Typography>
            {data.title && <Typography fontSize={'sm'}>{data.title}</Typography>}
            {data.company_name && data.name !== data.company_name && <Typography fontSize={'sm'}>{data.company_name}</Typography>}
        </Box>
    </Stack>
)
};

export default function BusinessEdit(props) {
    return (
        <ObjectEdit
            query={useGetBusiness}
            mutation={useUpdateBusiness}
            deleteMutation={useDeleteBusiness}
            type={'business'}
            permissionType={'businesses'}
            title={'Edit Business'}
            subtitleKey={'name'}
            color={'business'}
            {...props}
        >
            <ObjectColumn primary>
                <ObjectPrimaryInfoEdit
                    title={'Business Info'}
                    tint={'business'}
                    fields={fields}
                    // cardHeader={<CardHeader/>}
                />
            </ObjectColumn>

            <ObjectColumn>
                <ObjectRelationshipEdit type={'contacts'} permissions={'edit businesses'} />
                <ObjectRelationshipEdit type={'owned_properties'} permissions={'edit businesses'} />
                <ObjectRelationshipEdit type={'involvements'} permissions={'edit businesses'} />
            </ObjectColumn>

            <ObjectColumn>
                <ObjectRelationshipEdit type={'hours'} permissions={'edit businesses'} />
                <ObjectRelationshipEdit type={'notes'} permissions={'edit businesses'} />
                <ObjectRelationshipEdit type={'contact_reports'} />
                <ObjectRelationshipEdit type={'employees'} />
                <ObjectRelationshipEdit type={'files'} permissions={'edit businesses'} />
                <ObjectRelationship
                    type={'donation_entries'}
                    permissions={'view donations'}
                />
            </ObjectColumn>

            <ObjectColumn full>
                <ObjectRelationshipEdit type={'business_locations'} permissions={'edit businesses'} />
            </ObjectColumn>

        </ObjectEdit>
    )
}
